import { booleanAttribute } from "#ce/utils/props";
export const sizeOptions = ["xx-small", "x-small", "small", "default", "large"];
export const variantOptions = ["flat", "outlined", "text", "text-inline", "appearance-link"];
export const colorOptions = [
  "default",
  "primary",
  "secondary",
  "danger",
  "text",
  "error",
  "warning",
  "success",
  "info",
  "white",
  "surface",
  "custom",
];
export const densityOptions = ["default", "compact"];

// We had bugs in the past, where the props were mutatated. Apply Object.freeze() to prevent this.
export const props = Object.freeze({
  /**
   * Rendered tag. Use "a" for links (combined with `href`), "button" for actions.
   *
   * @type {String}
   * @default "button"
   * @values button, a
   * @storybook-control select
   */
  tag: {
    type: String,
    default: "button",
    validator: (value) => ["button", "a"].includes(value),
  },
  /**
   * What activating the button does to the form it sits in. `submit` submits that form and `reset`
   * resets it, each running the form's own validation first, the way a native button does.
   *
   * Unlike a native `<button>`, this defaults to `button`: the element does nothing to its form
   * unless asked, so a control placed inside a form for unrelated reasons stays inert.
   *
   * @type {String}
   * @default "button"
   * @values button, submit, reset
   */
  type: {
    type: String,
    default: "button",
    validator: (value) => ["button", "submit", "reset"].includes(value),
  },
  /**
   * Accessible name for the button, for when the visible content does not make a good one: an icon,
   * a bare number in a pager, a symbol. Takes precedence over `label`.
   *
   * @type {String}
   * @default undefined
   */
  accessibleName: {
    type: String,
    default: undefined,
  },
  /**
   * Button label text. When `icon` is also set, this becomes the `aria-label` for the icon-only button.
   *
   * @type {String}
   * @default undefined
   */
  label: {
    type: String,
    default: undefined,
  },
  /**
   * Theme colour applied to the button. "default" is the neutral base look with no theme colour applied; "primary"/"secondary" use brand colours; "danger"/"error"/"warning"/"success"/"info" use status colours; "text" inherits the surrounding text colour; "white" forces a white surface; "surface" uses the theme surface/on-surface tokens (neutral surface with on-surface text); "custom" defers to the consumer's CSS custom properties.
   *
   * @type {String}
   * @default "primary"
   * @values default, primary, secondary, danger, text, error, warning, success, info, white, surface, custom
   * @storybook-control select
   */
  color: {
    type: String,
    validator: (value) => colorOptions.includes(value),
    default: "primary",
  },
  /**
   * Button size. "xx-small" through "large" scale padding and font-size in tandem.
   *
   * @type {String}
   * @default "default"
   * @values xx-small, x-small, small, default, large
   * @storybook-control radio
   */
  size: {
    type: String,
    validator: (value) => sizeOptions.includes(value),
    default: "default",
  },
  /**
   * Visual emphasis level. "flat" is the default solid fill; "outlined" adds a border with transparent fill; "text" and "text-inline" remove the surface; "appearance-link" styles as an inline link.
   *
   * @type {String}
   * @default "flat"
   * @values flat, outlined, text, text-inline, appearance-link
   * @storybook-control select
   */
  variant: {
    type: String,
    validator: (value) => variantOptions.includes(value),
    default: "flat",
  },
  /**
   * Switch to a different variant on small screens.
   *
   * @type {String}
   * @default undefined
   * @values flat, outlined, text, text-inline, appearance-link
   * @storybook-control select
   * @storybook-table-subcategory Small Screen
   */
  smVariant: {
    type: String,
    validator: (value) => variantOptions.includes(value),
    default: undefined,
  },

  /**
   * Disable the button.
   *
   * @type {Boolean|String}
   * @default false
   */
  disabled: booleanAttribute(),
  /**
   * Marks the button as a toggle: the value is reflected as `aria-pressed` on
   * the inner button. Leave unset for non-toggle buttons — the attribute's
   * presence is what makes assistive technology announce a toggle.
   *
   * @type {Boolean|String}
   * @default undefined
   */
  pressed: booleanAttribute(undefined),
  /**
   * Marks the button inert while keeping it focusable: the value is reflected
   * as `aria-disabled` on the inner button, which stays enabled — so focus
   * survives the state flip and the caller's click guard does the ignoring.
   * Leave unset for buttons that are never inert.
   *
   * @type {Boolean|String}
   * @default undefined
   */
  ariaDisabled: booleanAttribute(undefined),
  /**
   * Icon name rendered as the only content. The button becomes round and `label` (when set) becomes the `aria-label`.
   *
   * @type {String|Boolean}
   * @default false
   * @storybook-control select
   */
  icon: {
    type: [String, Boolean],
    default: false,
  },
  /**
   * Show a loading spinner in place of the button content.
   *
   * @type {Boolean|String}
   * @default false
   */
  loading: booleanAttribute(),
  /**
   * Stretch the button to fill the available width.
   *
   * @type {Boolean|String}
   * @default false
   */
  block: booleanAttribute(),
  /**
   * Stretch the button to fill the available width on small screens.
   *
   * @type {Boolean|String}
   * @default false
   * @storybook-table-subcategory Small Screen
   */
  blockSm: booleanAttribute(),
  /**
   * Stack the prepend and append slots above and below the label instead of inline beside it.
   *
   * @type {Boolean|String}
   * @default false
   */
  stacked: booleanAttribute(),
  /**
   * Density (spacing) of the button. "compact" reduces vertical padding.
   *
   * @type {String}
   * @default "default"
   * @values default, compact
   * @storybook-control radio
   */
  density: {
    type: String,
    validator: (value) => densityOptions.includes(value),
    default: "default",
  },
  /**
   * Compact density on small screens (mobile). A deployment that shrinks its controls on
   * phones turns it on for every element that declares it through `sp_components.yml`.
   *
   * @type {Boolean|String}
   * @default false
   * @storybook-table-subcategory Small Screen
   */
  densitysmcompact: booleanAttribute(),
  /**
   * Prevent the button label from wrapping onto multiple lines.
   *
   * @type {Boolean|String}
   * @default true
   */
  noWrap: booleanAttribute(true),
  /**
   * Name of an `sp-icon` rendered in the prepend slot, before the label.
   *
   * @type {String}
   * @default undefined
   * @storybook-control select
   */
  prependIcon: {
    type: String,
    default: undefined,
  },
  /**
   * Name of an `sp-icon` rendered in the append slot, after the label.
   *
   * @type {String}
   * @default undefined
   * @storybook-control select
   */
  appendIcon: {
    type: String,
    default: undefined,
  },
});
